import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Alert, Typography, Chip, IconButton, TextField,
  MenuItem, FormControlLabel, Switch, Grid, Dialog, DialogContent, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { DataTable } from '../common/DataTable';
import { SearchableEntitySelect, EntityOption } from '../common/SearchableEntitySelect';
import { SearchableMultiEntitySelect } from '../common/SearchableMultiEntitySelect';
import { ModuleColumn } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  staffAPI, branchAPI, clientAPI, vehicleAPI, driverAPI, helperAPI, roleAPI,
} from '../../services/api';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';

const STAFF_ROLES = [
  { label: 'Manager', value: 'manager' },
  { label: 'Operator', value: 'operator' },
  { label: 'Supervisor', value: 'supervisor' },
  { label: 'Employee', value: 'employee' },
];

const columns: ModuleColumn[] = [
  { field: 'name', header: 'Name' },
  { field: 'staffRole', header: 'Role Type' },
  { field: 'branchName', header: 'Branch' },
  { field: 'roleName', header: 'App Role' },
  { field: 'clientNames', header: 'Clients' },
  { field: 'mobile', header: 'Mobile' },
  { field: 'designation', header: 'Designation' },
  {
    field: 'hasLogin',
    header: 'Login',
    render: (row) => (
      <Chip
        size="small"
        label={row.hasLogin ? 'Yes' : 'No'}
        sx={{
          fontWeight: 700,
          bgcolor: row.hasLogin ? brandCss.green100 : '#f1f5f9',
          color: row.hasLogin ? brandCss.primaryDark : brandColors.textMuted,
        }}
      />
    ),
  },
  { field: 'status', header: 'Status' },
];

const emptyForm = () => ({
  name: '',
  email: '',
  mobile: '',
  designation: '',
  staffRole: 'employee',
  branch: '',
  role: '',
  reportingManager: '',
  assignedClients: [] as string[],
  assignedVehicles: [] as string[],
  assignedDrivers: [] as string[],
  assignedHelpers: [] as string[],
  status: 'active',
  createLogin: false,
  password: '',
});

export const StaffPanel: React.FC = () => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());

  const [branches, setBranches] = useState<EntityOption[]>([]);
  const [clients, setClients] = useState<EntityOption[]>([]);
  const [vehicles, setVehicles] = useState<EntityOption[]>([]);
  const [drivers, setDrivers] = useState<EntityOption[]>([]);
  const [helpers, setHelpers] = useState<EntityOption[]>([]);
  const [roles, setRoles] = useState<EntityOption[]>([]);
  const [managers, setManagers] = useState<EntityOption[]>([]);

  const canCreate = hasPermission('staff', 'create');
  const canEdit = hasPermission('staff', 'edit');
  const canDelete = hasPermission('staff', 'delete');

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await staffAPI.getAll();
      setRows(res.data);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    Promise.all([
      branchAPI.getAll(),
      clientAPI.getAll(),
      vehicleAPI.getAll(),
      driverAPI.getAll(),
      helperAPI.getAll(),
      roleAPI.getAll(),
      staffAPI.getAll(),
    ]).then(([b, c, v, d, h, r, s]) => {
      setBranches((b.data || []).map((x: Record<string, string>) => ({
        _id: String(x._id), label: x.branchName, sublabel: x.branchCode,
      })));
      setClients((c.data || []).map((x: Record<string, string>) => ({
        _id: String(x._id), label: x.clientName || x.companyFullName, sublabel: x.companyFullName,
      })));
      setVehicles((v.data || []).map((x: Record<string, string>) => ({
        _id: String(x._id), label: x.vehicleNumber, sublabel: [x.vehicleType, x.brand].filter(Boolean).join(' · '),
      })));
      setDrivers((d.data || []).map((x: Record<string, string>) => ({
        _id: String(x._id), label: x.driverName, sublabel: x.driverCode,
      })));
      setHelpers((h.data || []).map((x: Record<string, string>) => ({
        _id: String(x._id), label: x.helperName, sublabel: x.helperCode,
      })));
      setRoles((r.data || []).map((x: Record<string, string>) => ({
        _id: String(x._id), label: x.roleName, sublabel: x.description,
      })));
      setManagers((s.data || [])
        .filter((x: Record<string, unknown>) => x.staffRole === 'manager')
        .map((x: Record<string, string>) => ({
          _id: String(x._id), label: x.name, sublabel: x.designation,
        })));
    }).catch(() => {});
  }, []);

  const branchFiltered = useMemo(() => ({
    vehicles,
    drivers,
    helpers,
  }), [vehicles, drivers, helpers]);

  const openCreate = () => {
    setForm(emptyForm());
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    const pickIds = (arr: unknown) => (Array.isArray(arr) ? arr.map((x) => String((x as { _id?: string })._id || x)) : []);
    setForm({
      name: String(row.name || ''),
      email: String(row.email || ''),
      mobile: String(row.mobile || ''),
      designation: String(row.designation || ''),
      staffRole: String(row.staffRole || 'employee'),
      branch: String((row.branch as { _id?: string })?._id || row.branch || ''),
      role: String((row.role as { _id?: string })?._id || row.role || ''),
      reportingManager: String((row.reportingManager as { _id?: string })?._id || row.reportingManager || ''),
      assignedClients: pickIds(row.assignedClients),
      assignedVehicles: pickIds(row.assignedVehicles),
      assignedDrivers: pickIds(row.assignedDrivers),
      assignedHelpers: pickIds(row.assignedHelpers),
      status: String(row.status || 'active'),
      createLogin: false,
      password: '',
    });
    setEditId(String(row._id));
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      if (form.createLogin && !form.role) {
        setError('App role (permissions) is required when creating login');
        return;
      }
      if (form.createLogin && !form.branch) {
        setError('Branch is required when creating login');
        return;
      }
      if (form.staffRole === 'manager' && !form.branch) {
        setError('Branch is required for Manager role');
        return;
      }
      if (form.staffRole === 'manager' && !form.assignedClients.length) {
        setError('Assign at least one client to the manager');
        return;
      }
      if (form.createLogin && !editId && (!form.password || form.password.length < 6)) {
        setError('Login password must be at least 6 characters');
        return;
      }
      const payload = {
        ...form,
        reportingManager: form.reportingManager || undefined,
        role: form.role || undefined,
      };
      if (editId) {
        await staffAPI.update(editId, payload);
        showToast('Staff updated');
      } else {
        await staffAPI.create(payload);
        showToast('Staff created');
      }
      setOpen(false);
      fetchRows();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: Record<string, unknown>) => {
    if (!window.confirm('Delete this staff member?')) return;
    try {
      await staffAPI.remove(String(row._id));
      showToast('Staff deleted');
      fetchRows();
    } catch {
      setError('Delete failed');
    }
  };

  const isManager = form.staffRole === 'manager';

  return (
    <Box>
      {canCreate && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ ...catchyPrimaryButtonSx, background: brandCss.gradientPrimary }}>
            Add Staff
          </Button>
        </Box>
      )}

      {error && !open && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onEdit={canEdit ? openEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        canEdit={canEdit}
        canDelete={canDelete}
        recordLabel="staff"
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: `${RADIUS}px` } }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: brandCss.green50 }}>
          <Typography fontWeight={800} fontSize={16}>{editId ? 'Edit Staff' : 'Add Staff'}</Typography>
          <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Staff Name" required fullWidth size="small" sx={catchyTextFieldSx}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Staff Role Type" required fullWidth size="small" sx={catchyTextFieldSx}
                value={form.staffRole} onChange={(e) => setForm({ ...form, staffRole: e.target.value })}>
                {STAFF_ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email" type="email" required fullWidth size="small" sx={catchyTextFieldSx}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Mobile" required fullWidth size="small" sx={catchyTextFieldSx}
                value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Designation" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SearchableEntitySelect label="Branch" placeholder="Select branch..."
                value={form.branch} options={branches} onChange={(v) => setForm({ ...form, branch: v })}
                required={isManager || form.createLogin} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SearchableEntitySelect label="App Permission Role" placeholder="Select role..."
                value={form.role} options={roles} onChange={(v) => setForm({ ...form, role: v })}
                required={form.createLogin} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SearchableEntitySelect label="Reporting Manager" placeholder="Optional..."
                value={form.reportingManager} options={managers}
                onChange={(v) => setForm({ ...form, reportingManager: v })} />
            </Grid>

            {isManager && (
              <>
                <Grid item xs={12}><Divider /><Typography fontWeight={800} fontSize={13} color={brandCss.primaryDark} sx={{ mt: 1 }}>Manager Assignments</Typography></Grid>
                <Grid item xs={12}>
                  <SearchableMultiEntitySelect label="Assigned Clients" placeholder="Clients this manager handles..."
                    value={form.assignedClients} options={clients} required
                    onChange={(ids) => setForm({ ...form, assignedClients: ids })} />
                </Grid>
                <Grid item xs={12}>
                  <SearchableMultiEntitySelect label="Assigned Vehicles" placeholder="Select vehicles..."
                    value={form.assignedVehicles} options={branchFiltered.vehicles}
                    onChange={(ids) => setForm({ ...form, assignedVehicles: ids })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SearchableMultiEntitySelect label="Assigned Drivers" placeholder="Select drivers..."
                    value={form.assignedDrivers} options={branchFiltered.drivers}
                    onChange={(ids) => setForm({ ...form, assignedDrivers: ids })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SearchableMultiEntitySelect label="Assigned Helpers" placeholder="Select helpers..."
                    value={form.assignedHelpers} options={branchFiltered.helpers}
                    onChange={(ids) => setForm({ ...form, assignedHelpers: ids })} />
                </Grid>
              </>
            )}

            <Grid item xs={12}><Divider /></Grid>
            {!editId && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={form.createLogin} onChange={(e) => setForm({ ...form, createLogin: e.target.checked })} />}
                    label="Create login account for this staff"
                  />
                </Grid>
                {form.createLogin && (
                  <Grid item xs={12} sm={6}>
                    <TextField label="Login Password" type="password" required fullWidth size="small" sx={catchyTextFieldSx}
                      value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                      helperText="Min 6 characters" />
                  </Grid>
                )}
              </>
            )}
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
              sx={{ ...catchyPrimaryButtonSx, background: brandCss.gradientPrimary }}>
              {editId ? 'Update Staff' : 'Save Staff'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
